import { Fragment } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { ResponsivePicture } from "@/components/landing/ResponsivePicture";
import { BlogShell } from "./BlogShell";
import { PostCard } from "./PostCard";
import type { BlogBlock, BlogPost, InlinePart } from "@/lib/blog";
import { getRelatedPosts, readingMinutes } from "@/lib/blog";

function RichText({ parts }: { parts: InlinePart[] }) {
  return parts.map((part, i) => {
    if (typeof part === "string") return <Fragment key={i}>{part}</Fragment>;
    if ("href" in part) {
      return (
        <a
          key={i}
          href={part.href}
          className="underline decoration-accent underline-offset-4 hover:text-accent"
        >
          {part.text}
        </a>
      );
    }
    return (
      <strong key={i} className="font-semibold">
        {part.strong}
      </strong>
    );
  });
}

function BlockView({ block }: { block: BlogBlock }) {
  if (block.kind === "h3") {
    return <h3 className="font-display text-2xl leading-tight text-primary">{block.text}</h3>;
  }
  if (block.kind === "ul") {
    return (
      <ul className="list-disc space-y-2 pl-6 text-lg leading-relaxed text-foreground">
        {block.items.map((item, i) => (
          <li key={i}>
            <RichText parts={item} />
          </li>
        ))}
      </ul>
    );
  }
  if (block.kind === "ol") {
    return (
      <ol className="list-decimal space-y-2 pl-6 text-lg leading-relaxed text-foreground">
        {block.items.map((item, i) => (
          <li key={i}>
            <RichText parts={item} />
          </li>
        ))}
      </ol>
    );
  }
  return (
    <p className="text-lg leading-relaxed text-foreground">
      <RichText parts={block.content} />
    </p>
  );
}

export function BlogArticle({ post }: { post: BlogPost }) {
  const related = getRelatedPosts(post.slug);
  const minutes = readingMinutes(post);

  return (
    <BlogShell>
      <main id="main-content" tabIndex={-1} className="pt-32 pb-24 md:pt-40 md:pb-32">
        <article className="mx-auto max-w-3xl px-6">
          <p className="mb-6 text-xs uppercase tracking-[0.28em] text-accent">
            <a href="/blog" className="hover:text-foreground">
              The Blog
            </a>
            <span className="mx-2 text-border" aria-hidden="true">
              /
            </span>
            {post.tag}
          </p>

          <h1 className="font-display text-4xl leading-[1.08] text-primary md:text-5xl">
            {post.title}
          </h1>

          <p className="mt-5 text-lg text-foreground">{post.excerpt}</p>

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/70 pt-5 text-xs uppercase tracking-widest text-muted-foreground">
            <span>Carolina Mountain Stamps</span>
            <span aria-hidden="true">·</span>
            <time dateTime={post.date}>{post.dateLabel}</time>
            <span aria-hidden="true">·</span>
            <span>{minutes} min read</span>
            <span aria-hidden="true">·</span>
            <span>Hendersonville, NC</span>
          </div>

          <div className="relative mt-10 overflow-hidden border border-border bg-card shadow-[var(--shadow-elegant)]">
            <ResponsivePicture
              {...post.featuredPicture}
              alt={post.imageAlt}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="aspect-[16/9] w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 border-[6px] border-dashed border-parchment/60 mix-blend-overlay" />
          </div>

          <div className="mt-12 space-y-10">
            {post.sections.map((section, i) => (
              <section key={section.heading || `intro-${i}`}>
                {section.heading ? (
                  <h2 className="font-display text-3xl leading-tight text-primary">
                    {section.heading}
                  </h2>
                ) : null}
                <div className={section.heading ? "mt-4 space-y-4" : "space-y-4"}>
                  {section.blocks?.length
                    ? section.blocks.map((block, j) => <BlockView key={j} block={block} />)
                    : section.paragraphs.map((p) => (
                        <p key={p.slice(0, 48)} className="text-lg leading-relaxed text-foreground">
                          {p}
                        </p>
                      ))}
                </div>
              </section>
            ))}
          </div>

          <aside className="relative mt-14 border border-border bg-card p-6 shadow-[var(--shadow-stamp)] md:p-8">
            <div className="pointer-events-none absolute inset-2 border border-dashed border-border/70" />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.28em] text-accent">From the shop</p>
              <h2 className="mt-2 font-display text-2xl text-primary">{post.cta.heading}</h2>
              <p className="mt-3 text-sm text-muted-foreground">{post.cta.body}</p>
              <a
                href={post.cta.href}
                target={post.cta.external ? "_blank" : undefined}
                rel={post.cta.external ? "noreferrer noopener" : undefined}
                className="group mt-6 inline-flex min-h-12 items-center gap-2 rounded-sm bg-accent px-5 py-3 text-sm font-medium text-accent-foreground shadow-[var(--shadow-stamp)] transition-transform hover:-translate-y-0.5"
              >
                {post.cta.label}
                {post.cta.external ? <span className="sr-only">(opens in a new tab)</span> : null}
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </a>
            </div>
          </aside>

          <p className="mt-10">
            <a
              href="/blog"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              All articles
            </a>
          </p>
        </article>

        {related.length > 0 ? (
          <section className="mx-auto mt-20 max-w-7xl px-6">
            <Reveal>
              <p className="mb-4 text-xs uppercase tracking-[0.28em] text-accent">Keep reading</p>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-3xl text-primary md:text-4xl">More from the shop</h2>
            </Reveal>
            <div className="mt-10 grid gap-8 md:grid-cols-2">
              {related.map((item, i) => (
                <Reveal key={item.slug} delay={i}>
                  <PostCard post={item} />
                </Reveal>
              ))}
            </div>
          </section>
        ) : null}
      </main>
    </BlogShell>
  );
}
